/**
 * Helipads, painted: a dark disc with a white ring and a white H, the legs of
 * the H along the pad's heading. One texture for every pad, drawn once.
 */

import {
  BufferAttribute,
  BufferGeometry,
  CanvasTexture,
  Color,
  DoubleSide,
  Mesh,
  MeshStandardMaterial,
  SRGBColorSpace,
} from 'three';
import type { PlacedHelipad } from '../core/build/helipads.js';
import { helipadDiscs } from '../core/build/helipads.js';

let material: MeshStandardMaterial | undefined;

function padMaterial(): MeshStandardMaterial {
  if (material !== undefined) return material;
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const c = size / 2;
  ctx.fillStyle = '#4a4d52';
  ctx.fillRect(0, 0, size, size);
  // The touchdown ring, then the H inside it.
  ctx.strokeStyle = '#f2f0ea';
  ctx.lineWidth = size * 0.035;
  ctx.beginPath();
  ctx.arc(c, c, size * 0.44, 0, Math.PI * 2);
  ctx.stroke();
  ctx.strokeStyle = '#e8c33a';
  ctx.lineWidth = size * 0.02;
  ctx.beginPath();
  ctx.arc(c, c, size * 0.39, 0, Math.PI * 2);
  ctx.stroke();
  ctx.fillStyle = '#f2f0ea';
  const leg = size * 0.075, tall = size * 0.42, gap = size * 0.2;
  ctx.fillRect(c - gap - leg / 2, c - tall / 2, leg, tall);
  ctx.fillRect(c + gap - leg / 2, c - tall / 2, leg, tall);
  ctx.fillRect(c - gap, c - leg / 2, gap * 2, leg);
  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  texture.anisotropy = 4;
  material = new MeshStandardMaterial({
    color: new Color(0xffffff),
    map: texture,
    roughness: 0.85,
    metalness: 0,
    side: DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });
  return material;
}

/** The tile's pads as one mesh, or undefined when it has none. */
export function buildHelipads(pads: readonly PlacedHelipad[] | undefined): Mesh | undefined {
  if (pads === undefined || pads.length === 0) return undefined;
  const disc = helipadDiscs(pads);
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(disc.positions, 3));
  geometry.setAttribute('uv', new BufferAttribute(disc.uvs, 2));
  const normals = new Float32Array(disc.positions.length);
  for (let i = 1; i < normals.length; i += 3) normals[i] = 1;
  geometry.setAttribute('normal', new BufferAttribute(normals, 3));
  geometry.setIndex(new BufferAttribute(disc.indices, 1));
  const mesh = new Mesh(geometry, padMaterial());
  mesh.renderOrder = 3;
  mesh.name = 'helipads';
  return mesh;
}
